"use client";
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const LIME = '#ceff1a';
const PASSED = '#ffffff';
const STEP_MS = 2200;

interface Stage {
    label: string;
    desc: string;
}

const stages: Stage[] = [
    { label: 'Audience', desc: 'Define the roles, industries and companies worth reaching.' },
    { label: 'Account Selection', desc: 'Shortlist the specific companies that matter most.' },
    { label: 'Messaging', desc: 'Craft creative that speaks to each buying role.' },
    { label: 'Campaign', desc: 'Launch across Sponsored Content, InMail and forms.' },
    { label: 'Lead Capture', desc: "Collect enquiries with LinkedIn's native lead forms." },
    { label: 'Qualification', desc: 'Filter and route leads to the right sales owner.' },
    { label: 'Optimisation', desc: 'Refine targeting and spend around what converts.' },
];

export default function LinkedInAdsProcess() {
    const [active, setActive] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setActive((prev) => (prev + 1) % stages.length);
        }, STEP_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    return (
        <section className="relative overflow-hidden py-20 md:py-28 px-6 md:px-16 lg:px-24" style={{ background: '#080400' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, rgba(165,180,252,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1200px] mx-auto relative z-10 flex flex-col items-center gap-16">
                <div className="flex flex-col items-center gap-6 max-w-[760px] mx-auto text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: LIME, fontFamily: CLASH }}
                    >
                        How We Work
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: '#fbfdfd' }}
                    >
                        Our LinkedIn Ads Process
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-base md:text-lg leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.02em' }}
                    >
                        We start with the audience and business outcome, then build campaigns around the people and companies most relevant to the opportunity.
                    </motion.p>
                </div>

                {/* Desktop timeline */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="hidden md:grid w-full relative"
                    style={{ gridTemplateColumns: `repeat(${stages.length}, 1fr)` }}
                >
                    {/* connecting line */}
                    <div className="absolute left-0 right-0 pointer-events-none" style={{ top: 21, height: 2, background: 'rgba(255,255,255,0.1)' }}>
                        <motion.div
                            className="h-full"
                            style={{ background: LIME, boxShadow: '0 0 10px rgba(206,255,26,0.6)' }}
                            animate={{ width: `${(active / (stages.length - 1)) * 100}%` }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                    </div>

                    {stages.map((stage, i) => {
                        const isActive = i === active;
                        const isPassed = i < active;
                        return (
                            <div key={stage.label} className="flex flex-col items-center text-center px-2 relative z-10">
                                <motion.div
                                    animate={{
                                        scale: isActive ? 1.15 : 1,
                                        background: isActive ? LIME : isPassed ? 'rgba(255,255,255,0.14)' : '#080400',
                                        borderColor: isActive ? LIME : isPassed ? PASSED : 'rgba(255,255,255,0.18)',
                                        boxShadow: isActive ? '0 0 18px rgba(206,255,26,0.55)' : '0 0 0px rgba(0,0,0,0)',
                                    }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                    className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
                                    style={{ fontFamily: CLASH, border: '2px solid', color: isActive ? '#080400' : isPassed ? PASSED : 'rgba(255,255,255,0.4)' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </motion.div>
                                <span
                                    className="mt-4 text-sm font-bold uppercase tracking-[0.03em]"
                                    style={{ fontFamily: CLASH, color: isActive ? LIME : isPassed ? PASSED : 'rgba(255,255,255,0.55)', transition: 'color 0.4s ease' }}
                                >
                                    {stage.label}
                                </span>
                                <span
                                    className="mt-2 text-xs leading-relaxed max-w-[150px]"
                                    style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}
                                >
                                    {stage.desc}
                                </span>
                            </div>
                        );
                    })}
                </motion.div>

                {/* Mobile vertical timeline */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex md:hidden flex-col w-full relative pl-2"
                >
                    <div className="absolute left-[23px] top-2 bottom-2 pointer-events-none" style={{ width: 2, background: 'rgba(255,255,255,0.1)' }}>
                        <motion.div
                            className="w-full"
                            style={{ background: LIME, boxShadow: '0 0 10px rgba(206,255,26,0.6)' }}
                            animate={{ height: `${(active / (stages.length - 1)) * 100}%` }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                    </div>

                    {stages.map((stage, i) => {
                        const isActive = i === active;
                        const isPassed = i < active;
                        return (
                            <div key={stage.label} className="flex items-start gap-4 relative z-10 pb-8 last:pb-0">
                                <motion.div
                                    animate={{
                                        scale: isActive ? 1.1 : 1,
                                        background: isActive ? LIME : isPassed ? 'rgba(255,255,255,0.14)' : '#080400',
                                        borderColor: isActive ? LIME : isPassed ? PASSED : 'rgba(255,255,255,0.18)',
                                        boxShadow: isActive ? '0 0 16px rgba(206,255,26,0.5)' : '0 0 0px rgba(0,0,0,0)',
                                    }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                    className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
                                    style={{ fontFamily: CLASH, border: '2px solid', color: isActive ? '#080400' : isPassed ? PASSED : 'rgba(255,255,255,0.4)' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </motion.div>
                                <div className="flex flex-col pt-2.5">
                                    <span
                                        className="text-sm font-bold uppercase tracking-[0.03em]"
                                        style={{ fontFamily: CLASH, color: isActive ? LIME : isPassed ? PASSED : 'rgba(255,255,255,0.55)', transition: 'color 0.4s ease' }}
                                    >
                                        {stage.label}
                                    </span>
                                    <span className="mt-1.5 text-xs leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}>
                                        {stage.desc}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </motion.div>
            </div>
        </section>
    );
}
